import React from 'react';
import type { Contract } from '../../types/contracts';

interface ContractApprovalsProps {
  contract: Contract;
}

const ContractApprovals: React.FC<ContractApprovalsProps> = ({ contract }) => {
  const getApprovalStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'green';
      case 'rejected':
        return 'red';
      case 'pending':
        return 'yellow';
      default:
        return 'gray';
    }
  };

  const getApprovalStatusIcon = (status: string) => {
    switch (status) {
      case 'approved':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        );
      case 'rejected':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        );
      case 'pending':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        );
      default:
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        );
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h3 className="text-lg font-medium text-gray-900">Approval Workflow</h3>
        <p className="text-sm text-gray-600 mt-1">Track the approval process for this contract</p>
      </div>

      {!contract.requiresApproval ? (
        <div className="text-center py-12">
          <svg
            className="w-16 h-16 text-gray-400 mx-auto mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1}
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No approval required</h3>
          <p className="text-gray-500">This contract does not require approval workflow</p>
        </div>
      ) : contract.approvals.length === 0 ? (
        <div className="text-center py-12">
          <svg
            className="w-16 h-16 text-gray-400 mx-auto mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1}
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No approvals configured</h3>
          <p className="text-gray-500">Approval workflow has not been set up for this contract</p>
        </div>
      ) : (
        <div className="space-y-4">
          {contract.approvals
            .sort((a, b) => a.order - b.order)
            .map(approval => {
              const statusColor = getApprovalStatusColor(approval.status);
              const isCurrentStep = contract.currentApprovalStep === approval.order - 1;

              return (
                <div
                  key={approval.userId}
                  className={`bg-white border rounded-lg p-4 ${
                    isCurrentStep ? 'border-blue-300 bg-blue-50' : 'border-gray-200'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3">
                      <div
                        className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-white bg-${statusColor}-500`}
                      >
                        {getApprovalStatusIcon(approval.status)}
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-3">
                          <p className="text-sm font-medium text-gray-900">
                            Step {approval.order}: {approval.role}
                          </p>
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-${statusColor}-100 text-${statusColor}-800`}
                          >
                            {approval.status.charAt(0).toUpperCase() + approval.status.slice(1)}
                          </span>
                          {isCurrentStep && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              Current Step
                            </span>
                          )}
                        </div>

                        {approval.approvedAt && (
                          <p className="text-xs text-gray-500 mt-1">
                            {approval.status === 'approved' ? 'Approved' : 'Rejected'} on{' '}
                            {new Date(approval.approvedAt).toLocaleDateString()}
                          </p>
                        )}

                        {approval.comments && (
                          <div className="mt-2 p-3 bg-gray-50 rounded-lg">
                            <p className="text-sm text-gray-700">{approval.comments}</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {approval.status === 'pending' && isCurrentStep && (
                      <div className="flex items-center space-x-2">
                        <span className="text-xs text-blue-600 font-medium">Action Required</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
        </div>
      )}

      {/* Approval Summary */}
      {contract.approvals.length > 0 && (
        <div className="mt-6 pt-6 border-t border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center">
              <div className="text-2xl font-semibold text-green-600">
                {contract.approvals.filter(a => a.status === 'approved').length}
              </div>
              <div className="text-sm text-gray-600">Approved</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-semibold text-yellow-600">
                {contract.approvals.filter(a => a.status === 'pending').length}
              </div>
              <div className="text-sm text-gray-600">Pending</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-semibold text-red-600">
                {contract.approvals.filter(a => a.status === 'rejected').length}
              </div>
              <div className="text-sm text-gray-600">Rejected</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContractApprovals;
